import React, { useMemo, useState } from "react";
import { Plus, Users } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  useDeleteTeamMutation,
  useGetCallFlowsQuery,
  useGetMemberAvailabilityQuery,
  useGetTeamsQuery,
} from "@/store/rest_api";
import { MemberAvailability } from "@/model/interface/Membership";
import Team from "@/model/interface/Team";
import { apiErrorMessage } from "@/util/apiError";
import TeamCard from "./TeamCard";
import TeamDialog from "./TeamDialog";

const AVAILABILITY_REFRESH_MS = 30_000;

interface TeamsTabProps {
  onCreateTeam: () => void;
}

export default function TeamsTab({ onCreateTeam }: TeamsTabProps) {
  const { data: teams = [], isLoading } = useGetTeamsQuery();
  const { data: availability = [] } = useGetMemberAvailabilityQuery(undefined, {
    pollingInterval: AVAILABILITY_REFRESH_MS,
  });
  const { data: callFlows = [] } = useGetCallFlowsQuery();
  const [deleteTeam] = useDeleteTeamMutation();
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editedTeam, setEditedTeam] = useState<Team | null>(null);
  const [teamToDelete, setTeamToDelete] = useState<Team | null>(null);

  const availabilityByMembershipId = useMemo(
    () =>
      new Map<string, MemberAvailability>(
        availability.map((member) => [String(member.id), member.availability ?? "OFFLINE"]),
      ),
    [availability],
  );

  const flowNamesByTeamId = useMemo(() => {
    const namesByTeamId = new Map<string, string[]>();
    callFlows.forEach((flow) => {
      flow.actions.forEach((action) => {
        const teamId = action.metadata?.ringTeamId;
        if (typeof teamId !== "string") {
          return;
        }
        const names = namesByTeamId.get(teamId) ?? [];
        if (!names.includes(flow.name)) {
          names.push(flow.name);
        }
        namesByTeamId.set(teamId, names);
      });
    });
    return namesByTeamId;
  }, [callFlows]);

  const openEdit = (team: Team) => {
    setEditedTeam(team);
    setIsEditOpen(true);
  };

  const confirmDelete = async () => {
    if (!teamToDelete) {
      return;
    }
    try {
      await deleteTeam(teamToDelete.id).unwrap();
      toast.success("Team deleted");
    } catch (error) {
      toast.error(apiErrorMessage(error, "Could not delete the team"));
    }
    setTeamToDelete(null);
  };

  if (isLoading) {
    return <div className="text-sm text-muted-foreground">Loading teams...</div>;
  }

  if (teams.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12 text-center">
          <Users className="mb-4 h-12 w-12 text-muted-foreground" />
          <h3 className="mb-2 text-lg font-semibold">No teams yet</h3>
          <p className="mb-4 max-w-md text-muted-foreground">
            Group people like Acquisitions or Closers. Calls can be transferred to a team, and ring steps in call flows
            can ring one.
          </p>
          <Button onClick={onCreateTeam}>
            <Plus className="mr-2 h-4 w-4" />
            Create your first team
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {teams.map((team) => (
          <TeamCard
            key={team.id}
            team={team}
            availabilityByMembershipId={availabilityByMembershipId}
            flowNames={flowNamesByTeamId.get(team.id) ?? []}
            onEdit={() => openEdit(team)}
            onDelete={() => setTeamToDelete(team)}
          />
        ))}
      </div>

      <TeamDialog open={isEditOpen} onOpenChange={setIsEditOpen} team={editedTeam} />

      <AlertDialog open={!!teamToDelete} onOpenChange={() => setTeamToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete team</AlertDialogTitle>
            <AlertDialogDescription>
              Delete <span className="font-semibold">{teamToDelete?.name}</span>? Call flow steps that ring this team
              will ring nobody until you pick members for them again.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
